import { VdpLayout } from '../types';

export interface SlugFormatOptions {
  sheetIndex?: number;
  totalSheets?: number;
  totalRecords?: number;
  date?: string;
  pageSize?: string;
  jobName?: string;
  templateWidthPt?: number;
  templateHeightPt?: number;
}

export function formatPrintSlug(
  layout: VdpLayout | undefined,
  record?: Record<string, any>,
  options?: SlugFormatOptions
): string {
  if (!layout) return '';

  const sheetNum = options?.sheetIndex ?? 1;
  const totalSheetsStr = options?.totalSheets ? `/${options.totalSheets}` : '';
  const totalRecs = options?.totalRecords ?? 1;
  const todayStr = options?.date || new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const sheetFormatName = layout.pageSize || options?.pageSize || 'Custom Sheet';
  const defaultJobName = options?.jobName || 'VDP_Print_Job';

  // Point to Millimeter conversion (1 pt = 0.352778 mm)
  const PT_TO_MM = 0.352778;

  // 1. Sheet Size
  const wMm = layout.pageWidth ? Math.round(layout.pageWidth * PT_TO_MM) : null;
  const hMm = layout.pageHeight ? Math.round(layout.pageHeight * PT_TO_MM) : null;
  const sheetSizeStr = wMm && hMm ? `${sheetFormatName} (${wMm}×${hMm}mm)` : sheetFormatName;

  // 2. Single-Up Size (1 Label)
  const tWpt = options?.templateWidthPt || 216;
  const tHpt = options?.templateHeightPt || 144;
  const tWMm = (tWpt * PT_TO_MM).toFixed(1);
  const tHMm = (tHpt * PT_TO_MM).toFixed(1);
  const singleUpStr = `${tWMm}×${tHMm}mm`;

  // 3. Multi-Up Size (Imposition Layout)
  const rows = Number(layout.rows) || 1;
  const cols = Number(layout.cols) || 1;
  const stickersPerSheet = layout.exportMode === 'single_pdf' || layout.exportMode === 'separate_pdfs' ? 1 : (rows * cols);
  const multiUpStr = `${rows}×${cols} (${stickersPerSheet}-Up)`;

  // 4. Bleed Size
  const bleedPt = Number(layout.bleed) || 0;
  const bleedMm = (bleedPt * PT_TO_MM).toFixed(1);
  const bleedStr = `${bleedMm}mm`;

  // 5. Margin Size
  const marginXPt = Number(layout.marginX) || 0;
  const marginYPt = Number(layout.marginY) || 0;
  const marginXMm = (marginXPt * PT_TO_MM).toFixed(1);
  const marginYMm = (marginYPt * PT_TO_MM).toFixed(1);
  const marginStr = `X:${marginXMm}mm/Y:${marginYMm}mm`;

  // 6. Safe Zone Size
  const safeZonePt = layout.safeZone !== undefined && !isNaN(Number(layout.safeZone))
    ? Number(layout.safeZone)
    : 8.5;
  const safeZoneMm = (safeZonePt * PT_TO_MM).toFixed(1);
  const safeZoneStr = `${safeZoneMm}mm`;

  const source = layout.printSlugSource || 'default';

  if (source === 'column' && layout.printSlugColumn) {
    const colVal = record && record[layout.printSlugColumn] !== undefined && record[layout.printSlugColumn] !== null
      ? String(record[layout.printSlugColumn]).trim()
      : '';
    const namePart = colVal ? `${layout.printSlugColumn}: ${colVal}` : `${layout.printSlugColumn}: (Empty)`;
    return `${namePart} | Sheet #${sheetNum}${totalSheetsStr} | SHEET: ${sheetSizeStr} | 1-UP: ${singleUpStr} | MULTI-UP: ${multiUpStr} | BLEED: ${bleedStr} | MARGIN: ${marginStr} | SAFE ZONE: ${safeZoneStr}`;
  }

  if (source === 'custom' && layout.printSlugText && layout.printSlugText.trim()) {
    let text = layout.printSlugText;

    const hasTags = /\{\{([^}]+)\}\}/.test(text);

    if (hasTags) {
      text = text.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
        const trimmedKey = key.trim();
        const lowerKey = trimmedKey.toLowerCase();

        if (lowerKey === 'sheet' || lowerKey === 'sheet_num' || lowerKey === 'sheetnumber') {
          return `${sheetNum}${totalSheetsStr}`;
        }
        if (lowerKey === 'sheet_size' || lowerKey === 'pagesize' || lowerKey === 'format') {
          return sheetSizeStr;
        }
        if (lowerKey === 'single_up' || lowerKey === 'single_up_size' || lowerKey === 'sticker_size') {
          return singleUpStr;
        }
        if (lowerKey === 'multi_up' || lowerKey === 'multi_up_size' || lowerKey === 'imposition' || lowerKey === 'grid') {
          return multiUpStr;
        }
        if (lowerKey === 'bleed' || lowerKey === 'bleed_size') {
          return bleedStr;
        }
        if (lowerKey === 'margin' || lowerKey === 'margin_size') {
          return marginStr;
        }
        if (lowerKey === 'safe_zone' || lowerKey === 'safezone' || lowerKey === 'safe_margin') {
          return safeZoneStr;
        }
        if (lowerKey === 'totalsheets' || lowerKey === 'total_sheets') {
          return String(options?.totalSheets || 1);
        }
        if (lowerKey === 'date' || lowerKey === 'today') {
          return todayStr;
        }
        if (lowerKey === 'total' || lowerKey === 'qty') {
          return String(totalRecs);
        }
        if (lowerKey === 'job' || lowerKey === 'jobname' || lowerKey === 'file') {
          return defaultJobName;
        }

        if (record && record[trimmedKey] !== undefined && record[trimmedKey] !== null) {
          return String(record[trimmedKey]);
        }

        return match;
      });
      return text;
    } else {
      return `JOB: ${text.trim()} | SHEET: ${sheetSizeStr} | 1-UP: ${singleUpStr} | MULTI-UP: ${multiUpStr} | BLEED: ${bleedStr} | MARGIN: ${marginStr} | SAFE ZONE: ${safeZoneStr} | SHEET #${sheetNum}${totalSheetsStr}`;
    }
  }

  // Default comprehensive slug string
  return `JOB: ${defaultJobName} | SHEET: ${sheetSizeStr} | 1-UP: ${singleUpStr} | MULTI-UP: ${multiUpStr} | BLEED: ${bleedStr} | MARGIN: ${marginStr} | SAFE ZONE: ${safeZoneStr} | SHEET #${sheetNum}${totalSheetsStr} | DATE: ${todayStr}`;
}
